import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
  CircularProgress,
  Skeleton,
  InputAdornment,
} from "@mui/material";
import { MoreVert, Logout, Edit, Visibility, VisibilityOff } from "@mui/icons-material";
import { User } from "@/types/common";
import ENDPOINTS from "../../api/endpoints";
import BottomNavBar from "@/common/BottomNavBar";
import useApiClient from "@/api/Client";
import { decodeToken } from "@/api/tokenManager";
import LogoutDlg from "@/components/profile/LogoutDlg";
import { toast } from "react-toastify";
import Icons from "@/assets";
import { getApiErrorMessage } from "@/utils/helper";
import { debounce } from "lodash";

const colorSet = {
  bg: "#E8F5E9",
  text: "#2E7D32",
  border: "#A5D6A7",
};

const styles = {
  pageContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    maxWidth: "100%",
    overflowX: "hidden",
    bgcolor: "white",
    background: "linear-gradient(180deg, #7747FB 2.63%, #F2F2F2 25.87%)",
  },
  avatar: {
    width: { xs: "12vw", sm: "48px" },
    height: { xs: "12vw", sm: "48px" },
    fontSize: { xs: "5vw", sm: "20px" },
    fontWeight: "600",
  },
  userName: {
    fontWeight: "700",
    color: "#FFFFFF",
    fontSize: { xs: "4.5vw", sm: "18px" },
  },
  inputField: {
    mb: { xs: "4vw", sm: "16px" },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: { xs: "4vw", sm: "16px" },
    },
  },
};

const Profile = () => {
  const { api } = useApiClient();
  const [userData, setUserData] = useState<User | null>(null);
  const [profileLoading, setProfileLoading] = useState<boolean>(true);
  const [savingProfile, setSavingProfile] = useState<boolean>(false);
  const { user_id } = decodeToken() ?? {};

  // Profile Edit State
  const [username, setUsername] = useState("");
  const [address, setAddress] = useState("");
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  // Email Edit State
  const [newEmail, setNewEmail] = useState("");
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [sendingEmailVerification, setSendingEmailVerification] = useState(false);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);

  // Password Reset State
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Menu / Logout State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const fetchUserProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      const res = await api.get(ENDPOINTS.USERS.GET_BY_ID(String(user_id)));
      const { user } = res?.data?.data ?? {};
      setUserData(user);
      setUsername(user?.username || "");
      setAddress(user?.address || "");
      setNewEmail(user?.email || "");
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setProfileLoading(false);
    }
  }, [api, user_id]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  useEffect(() => {
    const handleVisibilityChange = debounce(() => {
      if (document.visibilityState === "visible") {
        fetchUserProfile();
      }
    }, 5000, { leading: true, trailing: false });

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      handleVisibilityChange.cancel();
    };
  }, [fetchUserProfile]);

  const handleRequestEmailChange = useCallback(debounce(async () => {
    if (!newEmail.trim()) {
      toast.warning("Email is required");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.warning("Invalid email format");
      return;
    }

    setSendingEmailVerification(true);
    try {
      await api.post(ENDPOINTS.PROFILE.EMAIL_CHANGE_REQUEST, {
        new_email: newEmail.trim(),
      });
      setIsEditingEmail(false);
      setIsVerificationModalOpen(true);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Failed to send verification email"));
    } finally {
      setSendingEmailVerification(false);
    }
  }, 5000, { leading: true, trailing: false }), [newEmail, api]);

  const handleUpdateProfile = useCallback(debounce(async () => {
    if (!username.trim()) {
      toast.warning("Username is required");
      return;
    }
    const usernameRegex = /[a-zA-Z0-9]/;
    if (!usernameRegex.test(username.trim())) {
      toast.warning("Username cannot contain only special characters");
      return;
    }

    setSavingProfile(true);
    try {
      const payload: Partial<User> = {};
      if (username.trim() !== userData?.username) payload.username = username.trim();
      if (address.trim() !== (userData?.address || "")) payload.address = address.trim();

      if (Object.keys(payload).length > 0) {
        await api.patch(ENDPOINTS.PROFILE.MAIN, payload);
        toast.success("Profile updated successfully!");
      }
      setIsEditingProfile(false);
      fetchUserProfile();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Failed to update profile"));
    } finally {
      setSavingProfile(false);
    }
  }, 5000, { leading: true, trailing: false }), [username, address, userData, api, fetchUserProfile]);

  const handlePasswordReset = useCallback(debounce(async () => {
    if (!oldPassword.trim()) {
      toast.warning("Old Password is required");
      return;
    }
    if (!newPassword.trim()) {
      toast.warning("New Password is required");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      toast.error(
        "Password must contain at least 1 uppercase letter, 1 number, and 1 special character",
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (oldPassword === newPassword) {
      toast.error("New password must be different from the old password");
      return;
    }

    setSavingPassword(true);
    try {
      await api.put(ENDPOINTS.AUTH.CHANGE_PASSWORD, {
        oldPassword: oldPassword,
        newPassword: newPassword,
      });
      toast.success("Password changed successfully!");
      setResetModalOpen(false);
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Failed to change password"));
    } finally {
      setSavingPassword(false);
    }
  }, 5000, { leading: true, trailing: false }), [oldPassword, newPassword, confirmPassword, api]);

  return (
    <>
      <Box sx={styles.pageContainer}>
        <Card
          sx={{
            background: "transparent",
            overflow: "auto",
            boxShadow: "0",
            width: "94%",
            mx: "auto",
            mb: { xs: "20vw", sm: "80px" },
          }}
        >
          {profileLoading ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pt: "8%",
                  pb: "4%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    columnGap: "2vw",
                  }}
                >
                  <Skeleton variant="circular" sx={styles.avatar} />
                  <Box>
                    <Skeleton variant="text" width={120} height={32} sx={{ bgcolor: "rgba(255,255,255,0.35)", mb: 0.5 }} />
                    <Skeleton variant="text" width={80} height={20} sx={{ bgcolor: "rgba(255,255,255,0.35)" }} />
                  </Box>
                </Box>
                <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: "rgba(255,255,255,0.35)" }} />
              </Box>

              <Card
                sx={{
                  borderRadius: "3.2vw",
                  border: "0.25vw solid #d9d9d9",
                  boxShadow: "0",
                  overflow: "visible",
                }}
              >
                <CardContent sx={{ p: { xs: "4vw", sm: "16px" } }}>
                  <Skeleton variant="text" width="50%" height={40} sx={{ mb: { xs: "4vw", sm: "16px" } }} />
                  
                  <Skeleton variant="rectangular" height={60} sx={{ borderRadius: "8px", mb: { xs: "4vw", sm: "16px" } }} />
                  <Skeleton variant="rectangular" height={60} sx={{ borderRadius: "8px", mb: { xs: "4vw", sm: "16px" } }} />
                  <Skeleton variant="rectangular" height={60} sx={{ borderRadius: "8px", mb: { xs: "4vw", sm: "16px" } }} />
                  <Skeleton variant="rectangular" height={60} sx={{ borderRadius: "8px", mb: { xs: "6vw", sm: "24px" } }} />
                  
                  <Skeleton variant="rectangular" height={45} sx={{ borderRadius: "8vw" }} />
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Header Section from UserInfoCard */}
              <Box
                className="css-1xls8nz"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pt: "8%",
                  pb: "4%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    columnGap: "2vw",
                  }}
                >
                  <Avatar
                    sx={{
                      ...styles.avatar,
                      bgcolor: colorSet.bg,
                      border: `1px solid ${colorSet.border}`,
                      color: colorSet.text,
                    }}
                  >
                    {userData?.username?.[0]?.toUpperCase()}
                  </Avatar>
                  <Typography sx={styles.userName}>
                    {userData?.username}
                  </Typography>
                </Box>
                <IconButton onClick={handleMenuOpen} aria-label="menu">
                  <MoreVert sx={{ color: "#FFFFFF" }} fontSize="medium" />
                </IconButton>
              </Box>

              {/* Profile Details Edit Form */}
              <Card
                sx={{
                  borderRadius: "8px",
                  boxShadow: "0 4px 50px rgba(0, 0, 0, 0.05)",
                  overflow: "visible",
                  mt: 2,
                }}
              >
                <CardContent sx={{ p: { xs: "4vw", sm: "16px" } }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: "4vw", sm: "16px" } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Profile Details
                    </Typography>
                    {!isEditingProfile && (
                      <IconButton onClick={() => setIsEditingProfile(true)} size="small">
                        <Edit fontSize="small" sx={{ color: "#9443DF" }} />
                      </IconButton>
                    )}
                  </Box>

                  <Box sx={{ position: "relative", mb: { xs: "4vw", sm: "16px" }, border: "1px solid #ddd", borderRadius: "8px", p: { xs: "3vw", sm: "16px" } }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" sx={{ position: "absolute", top: "-10px", left: "12px", backgroundColor: "#fff", px: 0.5 }}>
                        Username
                      </Typography>
                      {!isEditingProfile ? (
                        <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>{userData?.username}</Typography>
                      ) : (
                        <TextField
                          fullWidth
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          sx={{ ...styles.inputField, mb: 0, mt: 0.5 }}
                          size="small"
                        />
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ position: "relative", mb: { xs: "4vw", sm: "16px" }, border: "1px solid #ddd", borderRadius: "8px", p: { xs: "3vw", sm: "16px" } }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" sx={{ position: "absolute", top: "-10px", left: "12px", backgroundColor: "#fff", px: 0.5 }}>
                        Address
                      </Typography>
                      {!isEditingProfile ? (
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: userData?.address ? 600 : 400, 
                            mt: 0.5,
                            color: userData?.address ? "inherit" : "textSecondary", 
                            fontStyle: userData?.address ? "normal" : "italic" 
                          }}
                        >
                          {userData?.address || "No address provided"}
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          placeholder="No address provided"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          sx={{ ...styles.inputField, mb: 0, mt: 0.5 }}
                          size="small"
                        />
                      )}
                    </Box>
                  </Box>

                  {isEditingProfile && (
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mb: 3 }}>
                      <Button
                        variant="text"
                        sx={{ textTransform: "none", color: "#666" }}
                        onClick={() => { 
                          setIsEditingProfile(false); 
                          setUsername(userData?.username || ""); 
                          setAddress(userData?.address || ""); 
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleUpdateProfile}
                        disabled={savingProfile || (username === userData?.username && address === (userData?.address || ""))}
                        sx={{
                          backgroundColor: "#9443DF",
                          color: "white",
                          textTransform: "none",
                          borderRadius: "8px",
                          "&:hover": { backgroundColor: "#7c35bd" },
                        }}
                      >
                        {savingProfile ? <CircularProgress size={20} color="inherit" /> : "Save"}
                      </Button>
                    </Box>
                  )}

                  <Box sx={{ position: "relative", mb: { xs: "4vw", sm: "24px" }, border: "1px solid #ddd", borderRadius: "8px", p: { xs: "3vw", sm: "16px" } }}>
                    {!isEditingEmail ? (
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="caption" color="textSecondary" sx={{ position: "absolute", top: "-10px", left: "12px", backgroundColor: "#fff", px: 0.5 }}>
                            Email
                          </Typography>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: userData?.email ? 600 : 400, 
                              mt: 0.5, 
                              color: userData?.email ? "inherit" : "textSecondary", 
                              fontStyle: userData?.email ? "normal" : "italic" 
                            }}
                          >
                            {userData?.email || "No email provided"}
                          </Typography>
                        </Box>
                        <IconButton onClick={() => setIsEditingEmail(true)} size="small">
                          <Edit fontSize="small" sx={{ color: "#9443DF" }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Typography variant="caption" color="textSecondary" sx={{ position: "absolute", top: "-10px", left: "12px", backgroundColor: "#fff", px: 0.5 }}>
                          New Email
                        </Typography>
                        <TextField
                          fullWidth
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          sx={{ ...styles.inputField, mb: 0, mt: 0.5 }}
                          size="small"
                        />
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
                          <Button
                            variant="text"
                            sx={{ textTransform: "none", color: "#666" }}
                            onClick={() => { setIsEditingEmail(false); setNewEmail(userData?.email || ""); }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleRequestEmailChange}
                            disabled={sendingEmailVerification || newEmail === userData?.email}
                            sx={{
                              backgroundColor: "#9443DF",
                              color: "white",
                              textTransform: "none",
                              borderRadius: "8px",
                              "&:hover": { backgroundColor: "#7c35bd" },
                            }}
                          >
                            {sendingEmailVerification ? <CircularProgress size={20} color="inherit" /> : "Verify & Save"}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {!resetModalOpen ? (
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setResetModalOpen(true)}
                      sx={{
                        borderColor: "#9443DF",
                        color: "#9443DF",
                        py: { xs: "3vw", sm: "12px" },
                        fontSize: { xs: "4vw", sm: "16px" },
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: "8px",
                      }}
                    >
                      Reset Password
                    </Button>
                  ) : (
                    <Box sx={{ mt: 2, p: { xs: "3vw", sm: "16px" }, border: "1px solid #ddd", borderRadius: "8px" }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Reset Password</Typography>
                      <TextField
                        label="Old Password"
                        type={showOldPassword ? "text" : "password"}
                        fullWidth
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        sx={styles.inputField}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowOldPassword(!showOldPassword)} edge="end">
                                {showOldPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label="New Password"
                        type={showNewPassword ? "text" : "password"}
                        fullWidth
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        sx={styles.inputField}
                        helperText="At least 8 characters, 1 uppercase, 1 number, 1 special char."
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label="Confirm New Password"
                        type={showConfirmPassword ? "text" : "password"}
                        fullWidth
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        sx={styles.inputField}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
                        <Button
                          variant="text"
                          sx={{ textTransform: "none", color: "#666" }}
                          onClick={() => {
                            setResetModalOpen(false);
                            setOldPassword("");
                            setNewPassword("");
                            setConfirmPassword("");
                          }}
                          disabled={savingPassword}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handlePasswordReset}
                          disabled={savingPassword}
                          sx={{
                            backgroundColor: "#9443DF",
                            color: "white",
                            textTransform: "none",
                            borderRadius: "8px",
                            "&:hover": { backgroundColor: "#7c35bd" },
                          }}
                        >
                          {savingPassword ? <CircularProgress size={20} color="inherit" /> : "Save"}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </Card>

        <BottomNavBar />
      </Box>

      {/* Menu / Logout Dialog */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2, minWidth: 150 },
        }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setConfirmLogoutOpen(true);
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
      <LogoutDlg
        open={confirmLogoutOpen}
        onClose={() => setConfirmLogoutOpen(false)}
      />

      <Dialog
        open={isVerificationModalOpen}
        onClose={() => setIsVerificationModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px" },
        }}
      >
        <DialogContent sx={{ p: 4, textAlign: "center" }}>
          <Box sx={{ mb: 2 }}>
            <img src={Icons.EmailVerified} alt="Email Verified" style={{ width: "80px", height: "80px" }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#191919", mb: 1 }}>
            Verification Email Sent
          </Typography>
          <Typography variant="body1" sx={{ color: "#626262", mb: 3 }}>
            We've sent a verification mail to <b>{newEmail}</b>. Please click the link in the email to verify and update your profile.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setIsVerificationModalOpen(false)}
            sx={{
              backgroundColor: "#9443DF",
              color: "white",
              textTransform: "none",
              borderRadius: "8px",
              py: 1.5,
              fontWeight: 500,
              "&:hover": { backgroundColor: "#7c35bd" },
            }}
          >
            Got it
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Profile;
